import React, { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Import the plugin for jsPDF
import IconButton from '../../elements/IconButton';
import Button from '../../elements/Button';

const Main = (props) => {
    const [loadedData, setLoadedData] = useState([]);
    const [stillGettingData, setStillGettingData] = useState(0);
    const isCancelled = useRef(false); // Used to track if fetching was cancelled

    const handleCancelFetch = () => {
        isCancelled.current = true; // Set flag to true when fetching is cancelled
    };

    const fetchAllData = (exportType) => {
        let skip = 0;
        const limit = 15;
        let allData = [];
        setStillGettingData(1);

        const fetchNext = () => {
            if (isCancelled.current) {
                console.log("Fetching has been cancelled by the user.");
                setStillGettingData(0); // Update state to reflect cancellation
                return;
            }
            getData(skip, limit, exportType, (hasMoreData, newData) => {
                console.log(newData)
                if (newData !== undefined) {
                    allData = allData.concat(newData);
                    skip += limit;
                    fetchNext();
                } else {
                    if (exportType === 'CSV') {
                        downloadCSV(allData, props.exportKeys, props.fileTitle);
                    } else if (exportType === 'PDF') {
                        downloadPDF(allData, props.exportKeys, props.fileTitle);
                    }
                    setStillGettingData(2);
                }
            });
        };

        fetchNext();
    };

    const getData = (skip, limit, exportType, onCompletion) => {
        var myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem('access_token'));
        myHeaders.append("Content-Type", "application/json");

        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            redirect: 'follow',
            body: JSON.stringify(props.filterData)
        };

        props.tokenSafeAPIRequest(
            `${props.url}${props.specific ? '/' + props.specific : ''}?limit=${limit}&fetch_links=true&skip=${skip}&sort_field=${props.sort}&start_date=${props.calStartDate}&end_date=${props.calEndDate}&active_only=false&export_type=${exportType}`,
            requestOptions,
            '',
            (result, status) => {
                if ('postProcessing' in props) {
                    result = props.postProcessing(result);
                }

                console.log(result)

                if ('reports' in result) {
                    processResult(result.reports, onCompletion);
                } else if ('accounts' in result) {
                    processResult(result.accounts, onCompletion);
                } else if ('transactions' in result) {
                    processResult(result.transactions, onCompletion);
                } else if ('dealerships' in result) {
                    processResult(result.dealerships, onCompletion);
                } else if ('spiffs' in result) {
                    processResult(result.spiffs, onCompletion);
                } else if ('dppps_and_gap' in result) {
                    processResult(result.dppps_and_gap, onCompletion);
                } else {
                    processResult(result, onCompletion);
                }
            }
        );
    };

    const processResult = (data, onCompletion) => {
        if (data && data.length > 0) {
            setLoadedData((loadedData) => [...loadedData, ...data]);
            onCompletion(true, data);
        } else {
            onCompletion(false);
        }
    };

    function formatNumber(value) {
        try {
            // Attempt to convert the input to a number
            const num = parseFloat(value);
            // Check if the conversion resulted in a valid number
            if (!isNaN(num)) {
              // Use Intl.NumberFormat for US-style number formatting with two decimal places
              return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(num);
            } else {
              // Return a default value if the input is not a valid number
              return '0.00';
            }
          } catch (e) {
            // In case of any unexpected errors, return a default value
            return '0.00';
          }
    }

    const convertPayeeToUser = (payee, user, spiffee, dealership) => {
        if (payee === null) {return 'No User'}
        if (payee === 0) {return spiffee.name}
        if (payee === 1) {return dealership.name}
        if (payee === 2) {return user === null ? 'No Agent':(user?.first_name + ' ' + user?.last_name)}
        if (payee === 3) {return 'Inside Sales'}
    }

    const downloadCSV = (data, keys, filename = 'data.csv') => {

        const csvEscape = (value) => {
            if (value === null || value === undefined) return '';
                const str = String(value);
            if (/[",\n\r]/.test(str)) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        console.log(data)

        let csvContent = "";
        let lastUserId = null;
        let total = 0;

        csvContent += "Agent,Dealership,Admin,Report Type,Contract Type,QTY,CC,Net,Total Net,Inside Sales,Reason\r\n";
        data.forEach((report) => {
            const row = [
                `${report?.subagent_ID?.first_name} ${report?.subagent_ID?.last_name}`,
                report?.dealership_ID?.name,
                report?.admin_ID?.name,
                report.report_type,
                report.contract_type,
                report.quantity,
                report.total_contract_count,
                report.net,
                report.net * report.quantity,
                report.inside_sales ? 'YES' : 'NO',
                report.reason,
            ]
                .map(csvEscape)
                .join(',');

            csvContent += row + '\r\n';
        });
    
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };
    
    const downloadPDF = (data, keys, filename = 'data.pdf') => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text(props.fileTitle || 'Report', 20, 10);

        // Group data by report_type
        const groupedData = data.reduce((acc, transaction) => {
            const category = transaction.report_type || 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(transaction);
            return acc;
        }, {});

        const addPageNumbers = (doc) => {
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height;
            const pageWidth = pageSize.width;

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.text(`Page ${i}`, pageWidth - 30, pageHeight - 10);
            }
        };

        // Sort each category by subagent_ID or user_ID for consistency
        for (const category in groupedData) {
            groupedData[category].sort((a, b) => 
                (a.subagent_ID?.first_name > b.subagent_ID?.first_name ? 1 : -1)
            );
        }

        // ✅ Sort categories alphabetically (numbers before letters)
        const sortedCategories = Object.keys(groupedData).sort((a, b) => {
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        });

        // Loop through each category and render its table
        let currentY = 20;
        sortedCategories.forEach((category, idx) => {
            const transactions = groupedData[category];

            if (idx > 0) doc.addPage();
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(`Category: ${category}`, 14, 20);
            doc.setFont('helvetica', 'normal');

            const tableData = transactions.map(t => [
                t?.subagent_ID?.first_name + ' ' + t?.subagent_ID?.last_name,
                t?.dealership_ID?.name,
                t?.admin_ID?.name,
                t?.report_type,
                t?.contract_type,
                t?.quantity,
                t?.total_contract_count,
                '$' + formatNumber(t?.net),
                '$' + formatNumber(t?.net * t?.quantity),
                t?.inside_sales ? "YES" : "NO",
                t?.reason,
            ]);

            const table = doc.autoTable({
                startY: 30,
                head: [['Agent', 'Dealership', 'Admin', 'Report Type', 'Contract Type', 'QTY', 'CC', 'Net', 'Total Net', 'Inside Sales', 'Reason']],
                body: tableData,
                styles: {
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                },
                headStyles: {
                    fillColor: false,
                    textColor: 0,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                },
                columnStyles: {
                    10: { cellWidth: 40 }, // ⬅ limit Reason column width
                },
                theme: 'striped',
                wrap: true,
            });

            // calculate total for this category
            const totalAmount = transactions.reduce((sum, t) => sum + ((t.net || 0) * (t.quantity || 0)), 0);

            // add total line after the table
            const finalY = table.lastAutoTable.finalY + 10;
            doc.setFont('helvetica', 'bold');
            doc.text(`Total ${category} - $${formatNumber(totalAmount)}`, 14, finalY);
            doc.setFont('helvetica', 'normal');
        });

        addPageNumbers(doc);
        doc.save(filename);

        return groupedData; // return it if you want to inspect or reuse
    };
    

    return (
        <div style={{ width: '360px', height: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexBasis: '10%', display: 'flex', flexDirection: 'row' }}>
                <span style={{ fontWeight: '500', fontSize: '20px', flexBasis: '99%' }}>Export Report</span>
                <IconButton img={false} colors={props.colors} Style={{ borderRadius: '3px', backgroundColor: props.colors.border, width: '24px' }} size="16px">
                    <div onClick={() => { handleCancelFetch(); props.setIsOpen() }} style={{ paddingLeft: '3px', paddingRight: '3px' }}><span onClick={() => { handleCancelFetch(); props.setIsOpen() }}>x</span></div>
                </IconButton>
            </div>
            <div style={{ flexBasis: '90%', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px' }}>Downloaded Rows: {loadedData.length}</span>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('CSV')}>Download All CSV</Button>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('PDF')}>Download All PDF</Button>
            </div>
        </div>
    );
};

export default Main;
